export const POWER_BI_FILTER_STATE_VERSION = 1;
export const POWER_BI_FILTER_STATE_STORAGE_PREFIX = 'kwello:powerbi-filter-state:v1';

export interface PowerBiFilterStateStorageKeyParts {
  userKey: string;
  projectId: string;
  reportId: string;
}

export interface PowerBiStoredSlicerState {
  visualName: string;
  title?: string;
  targetKey: string;
  state: unknown;
}

export interface PowerBiStoredPageState {
  pageFilters: unknown[];
  slicers: PowerBiStoredSlicerState[];
}

export interface PowerBiFilterStateSnapshot {
  version: typeof POWER_BI_FILTER_STATE_VERSION;
  updatedAt: string;
  userChangedDate: boolean;
  reportFilters: unknown[];
  sharedDateSlicers: PowerBiStoredSlicerState[];
  pages: Record<string, PowerBiStoredPageState>;
}

interface CreateSnapshotArgs {
  reportFilters?: unknown[];
  sharedDateSlicers?: PowerBiStoredSlicerState[];
  pages?: Record<string, PowerBiStoredPageState>;
  updatedAt?: string;
  userChangedDate?: boolean;
}

type UnknownRecord = Record<string, unknown>;

export const buildPowerBiFilterStateStorageKey = ({
  userKey,
  projectId,
  reportId,
}: PowerBiFilterStateStorageKeyParts) =>
  // Contract: Date state is shared only within this user + project + report tuple.
  // Page name is intentionally excluded so pages from the same report can share it.
  `${POWER_BI_FILTER_STATE_STORAGE_PREFIX}:${userKey}:${projectId}:${reportId}`;

export const createPowerBiFilterStateSnapshot = ({
  reportFilters = [],
  sharedDateSlicers = [],
  pages = {},
  updatedAt = new Date().toISOString(),
  userChangedDate = false,
}: CreateSnapshotArgs = {}): PowerBiFilterStateSnapshot => ({
  version: POWER_BI_FILTER_STATE_VERSION,
  updatedAt,
  userChangedDate,
  reportFilters,
  sharedDateSlicers,
  pages,
});

export const loadPowerBiFilterStateSnapshot = (
  storage: Storage,
  key: string
): PowerBiFilterStateSnapshot | null => {
  try {
    const rawValue = storage.getItem(key);

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<PowerBiFilterStateSnapshot>;

    if (!isValidPowerBiFilterStateSnapshot(parsed)) {
      storage.removeItem(key);
      return null;
    }

    return {
      ...parsed,
      userChangedDate: parsed.userChangedDate ?? false,
      sharedDateSlicers: parsed.sharedDateSlicers ?? [],
    };
  } catch {
    removeStorageItem(storage, key);
    return null;
  }
};

export const savePowerBiFilterStateSnapshot = (
  storage: Storage,
  key: string,
  snapshot: PowerBiFilterStateSnapshot
) => {
  try {
    storage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Browser storage can fail because of quota, privacy settings, or unavailable storage.
  }
};

export const clearPowerBiFilterStateSnapshot = (storage: Storage, key: string) => {
  removeStorageItem(storage, key);
};

export const upsertPowerBiPageState = (
  snapshot: PowerBiFilterStateSnapshot,
  pageName: string,
  pageState: PowerBiStoredPageState,
  updatedAt = new Date().toISOString()
): PowerBiFilterStateSnapshot => ({
  ...snapshot,
  updatedAt,
  pages: {
    ...snapshot.pages,
    [pageName]: pageState,
  },
});

export const getPowerBiSlicerTargetKey = (slicerState: unknown): string => {
  const slicerRecord = asRecord(slicerState);

  if (!slicerRecord) {
    return '';
  }

  const filterTarget = getFirstTargetFromCollection(slicerRecord.filters);

  if (filterTarget) {
    return getPowerBiTargetKey(filterTarget);
  }

  const slicerTarget = getFirstTargetFromCollection(slicerRecord.targets);

  if (slicerTarget) {
    return getPowerBiTargetKey(slicerTarget);
  }

  return '';
};

const isValidPowerBiFilterStateSnapshot = (
  snapshot: Partial<PowerBiFilterStateSnapshot>
): snapshot is PowerBiFilterStateSnapshot =>
  snapshot.version === POWER_BI_FILTER_STATE_VERSION &&
  typeof snapshot.updatedAt === 'string' &&
  (snapshot.userChangedDate === undefined || typeof snapshot.userChangedDate === 'boolean') &&
  Array.isArray(snapshot.reportFilters) &&
  (snapshot.sharedDateSlicers === undefined || Array.isArray(snapshot.sharedDateSlicers)) &&
  isRecord(snapshot.pages);

const getFirstTargetFromCollection = (collection: unknown): unknown => {
  if (!Array.isArray(collection)) {
    return null;
  }

  for (const item of collection) {
    const itemRecord = asRecord(item);

    if (!itemRecord) {
      continue;
    }

    if (itemRecord.target) {
      return itemRecord.target;
    }

    return itemRecord;
  }

  return null;
};

const getPowerBiTargetKey = (target: unknown): string => {
  const targetRecord = asRecord(target);

  if (!targetRecord) {
    return '';
  }

  const table = toStringValue(targetRecord.table);
  const column = toStringValue(targetRecord.column);
  const hierarchy = toStringValue(targetRecord.hierarchy);
  const hierarchyLevel = toStringValue(targetRecord.hierarchyLevel);
  const measure = toStringValue(targetRecord.measure);

  if (table && column) {
    return `${table}.${column}`;
  }

  if (table && hierarchy && hierarchyLevel) {
    return `${table}.${hierarchy}.${hierarchyLevel}`;
  }

  if (table && measure) {
    return `${table}.${measure}`;
  }

  return stableStringify(targetRecord);
};

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
};

const toStringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

const asRecord = (value: unknown): UnknownRecord | null => (isRecord(value) ? value : null);

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const removeStorageItem = (storage: Storage, key: string) => {
  try {
    storage.removeItem(key);
  } catch {
    // Browser storage can fail because of privacy settings or unavailable storage.
  }
};
